import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { productosService, type ProductoDto } from '@/services/api/productos.service';
import { clientesService, type ClienteDto } from '@/services/api/clientes.service';
import { comprobantesService } from '@/services/api/comprobantes.service';
import type { ApiResponse, PaginatedResponse } from '@/services/api/api.types';
import { useOnboardingStore } from '../store/useOnboardingStore';

/** Código fijo con el que el propio backend siembra "Productos Varios" al crear la empresa. */
const CODIGO_PRODUCTO_POR_DEFECTO = 'P00001';

/**
 * Toda empresa nueva nace con "Productos Varios" (código P00001) ya sembrado
 * por el backend, así que un total de 1 no alcanza para saber si el usuario
 * agregó algo real: hay que revisar si ese único registro es justo el
 * genérico. Con 2 o más ya hay al menos un producto real, sea que el
 * genérico siga existiendo o que el usuario lo haya borrado.
 */
function hasRealProduct(res: ApiResponse<ProductoDto[]>): boolean {
  const total = res.paginacion?.total ?? 0;
  if (total > 1) return true;
  if (total === 0) return false;
  return res.data?.[0]?.codigo !== CODIGO_PRODUCTO_POR_DEFECTO;
}

/**
 * Mismo caso que con productos: toda empresa nueva nace con un "Cliente
 * General" ya sembrado. Se identifica por el flag de dominio `esPorDefecto`
 * (más estable que comparar por número de documento).
 *
 * El total real viene en `paginacion.total` (nivel superior), igual que en
 * productos y comprobantes -no dentro de `data.meta` como declara el tipo
 * `PaginatedResponse`, que no coincide con la forma real de la respuesta-.
 * `res.data` en la práctica también varía entre arreglo plano y `{ data: [] }`
 * según el endpoint; se maneja igual que ya lo hace `useClientes.ts`.
 */
function hasRealCustomer(res: ApiResponse<ClienteDto[] | PaginatedResponse<ClienteDto>>): boolean {
  const lista = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
  const total = res.paginacion?.total ?? lista.length;
  if (total > 1) return true;
  if (total === 0) return false;
  return lista[0]?.esPorDefecto !== true;
}

/** Cada cuánto se refresca en segundo plano mientras el checklist es relevante. */
const REFRESH_INTERVAL_MS = 20_000;

/**
 * No existe (todavía) un endpoint de backend que devuelva el progreso ya
 * calculado, así que se arma con llamadas livianas (pageSize=1) en paralelo.
 * Se repite: en cada cambio de ruta, cada REFRESH_INTERVAL_MS como respaldo,
 * y al instante cuando algo llama a useOnboardingStore.getState().requestRefresh()
 * (ver los puntos de creación de cliente/producto, que suceden en un modal
 * sin cambiar de ruta). Se detiene apenas la empresa queda "retirada" (ya
 * emitió), para no seguir consultando sin necesidad.
 *
 * El paso "company" no revisa campos: crear la empresa (la primera al
 * registrarse, o una adicional desde "Nueva Empresa") ya exige lo mínimo
 * necesario en ese mismo formulario, así que si `companyId` llegó hasta acá
 * es porque la empresa ya existe y el paso se da por completo.
 */
export function useOnboardingProgress(companyId: string | null): void {
  const setProgress = useOnboardingStore((s) => s.setProgress);
  const isRetired = useOnboardingStore((s) =>
    companyId ? s.isRetired(companyId) : false,
  );
  const refreshSignal = useOnboardingStore((s) => s.refreshSignal);
  const location = useLocation();

  useEffect(() => {
    if (!companyId || isRetired) return;
    let cancelled = false;

    const fetchProgress = () => {
      if (document.visibilityState === 'hidden') return;
      Promise.all([
        productosService.listar({ page: 1, pageSize: 1 }),
        clientesService.getAll({ page: 1, pageSize: 1 }),
        comprobantesService.getAll({ page: 1, pageSize: 1 }),
      ])
        .then(([productosRes, clientesRes, comprobantesRes]) => {
          if (cancelled) return;
          setProgress({
            companyId,
            company: true,
            product: hasRealProduct(productosRes),
            customer: hasRealCustomer(clientesRes),
            hasIssuedInvoice: (comprobantesRes.paginacion?.total ?? 0) > 0,
          });
        })
        .catch(() => {
          // Sin conexión o error puntual: el checklist simplemente no actualiza
          // su progreso en este intento, se reintenta en el próximo ciclo.
        });
    };

    fetchProgress();
    const interval = window.setInterval(fetchProgress, REFRESH_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [companyId, isRetired, location.pathname, refreshSignal, setProgress]);
}
